import React, { useEffect, useRef, useState, useCallback } from 'react';
import api from '../api';
import { loadYmaps } from '../utils/loadYmaps';
import './AddressMapPicker.css';

const DEFAULT_CENTER = { lat: 55.751244, lon: 37.618423 }; // Moscow fallback, used only before a city is picked

// Interactive Yandex Maps v3 picker used inside AddressAutocomplete.
// Draggable/clickable pin -> reverse geocode -> onSelect(AddressResult) with the same shape
// the backend /geocoding/* endpoints already return (displayName, latitude, longitude, components).
const AddressMapPicker = ({ center, onSelect, disabled }) => {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [status, setStatus] = useState('loading'); // loading | ready | error
    const [errorMsg, setErrorMsg] = useState(null);
    const [geocoding, setGeocoding] = useState(false);

    const reverseGeocode = useCallback(async (lat, lon) => {
        setGeocoding(true);
        try {
            const res = await api.get('/geocoding/reverse', { params: { lat, lon } });
            onSelect?.(res.data);
        } catch (e) {
            console.error('Reverse geocode error:', e);
        } finally {
            setGeocoding(false);
        }
    }, [onSelect]);

    // init map once
    useEffect(() => {
        let cancelled = false;

        loadYmaps().then((ymaps3) => {
            if (cancelled || !containerRef.current) return;
            const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapMarker, YMapListener } = ymaps3;

            const start = center ?? DEFAULT_CENTER;

            const map = new YMap(containerRef.current, {
                location: { center: [start.lon, start.lat], zoom: center ? 16 : 10 },
            });
            map.addChild(new YMapDefaultSchemeLayer());
            map.addChild(new YMapDefaultFeaturesLayer({ zIndex: 1800 }));

            const pinEl = document.createElement('div');
            pinEl.className = 'address-map-picker__pin';

            const marker = new YMapMarker(
                {
                    coordinates: [start.lon, start.lat],
                    draggable: !disabled,
                    onDragEnd: (event) => {
                        const coords = event?.coordinates ?? marker.coordinates;
                        if (!coords) return;
                        const [lon, lat] = coords;
                        reverseGeocode(lat, lon);
                    },
                },
                pinEl
            );
            map.addChild(marker);

            if (!disabled) {
                const listener = new YMapListener({
                    layerId: 'any',
                    onClick: (_object, event) => {
                        if (!event?.coordinates) return;
                        const [lon, lat] = event.coordinates;
                        marker.update({ coordinates: [lon, lat] });
                        reverseGeocode(lat, lon);
                    },
                });
                map.addChild(listener);
            }

            mapRef.current = map;
            markerRef.current = marker;
            setStatus('ready');
        }).catch((e) => {
            console.error('Failed to load Yandex Maps:', e);
            if (!cancelled) { setStatus('error'); setErrorMsg(e.message); }
        });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // react to external center changes (city/street picked via AddressAutocomplete) without re-triggering reverse geocode
    useEffect(() => {
        if (!mapRef.current || !markerRef.current || !center) return;
        mapRef.current.update({ location: { center: [center.lon, center.lat], zoom: 16 } });
        markerRef.current.update({ coordinates: [center.lon, center.lat] });
    }, [center?.lat, center?.lon]);

    if (status === 'error') {
        return (
            <div className="address-map-picker__error">
                Не удалось загрузить карту{errorMsg ? `: ${errorMsg}` : ''}.
            </div>
        );
    }

    return (
        <div className="address-map-picker">
            <div ref={containerRef} className="address-map-picker__map" />
            {(status === 'loading' || geocoding) && (
                <div className="address-map-picker__overlay">
                    {status === 'loading' ? 'Загрузка карты…' : 'Определяем адрес…'}
                </div>
            )}
            {!disabled && (
                <p className="address-map-picker__hint">Перетащите метку или кликните на карте, чтобы уточнить адрес</p>
            )}
        </div>
    );
};

export default AddressMapPicker;
